import { type FC } from "react";

import { Button } from "./button";

export type UploadChoiceOption<T extends string> = {
  value: T;
  label: string;
  hint?: string;
  // A grey line under the option.
  note?: string;
};

// After he picks a file: what it's for, one choice, then Upload. Nothing is
// sent before he says.
export const UploadChoice = <T extends string>({
  fileName,
  options,
  value,
  onChange,
  onCancel,
  onUpload,
}: {
  fileName: string;
  options: ReadonlyArray<UploadChoiceOption<T>>;
  value: T;
  onChange(value: T): void;
  onCancel(): void;
  onUpload(): void;
}): ReturnType<FC> => (
  <fieldset className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4">
    <legend className="sr-only">Use {fileName} for</legend>
    <p className="text-sm text-slate-700">
      Use <span className="font-medium text-slate-900">{fileName}</span> for
    </p>
    {options.map((option) => (
      <label
        key={option.value}
        className="flex flex-col text-sm text-slate-700"
      >
        <span className="flex items-center gap-2">
          <input
            type="radio"
            name="upload-choice"
            checked={value === option.value}
            onChange={() => onChange(option.value)}
            className="size-4 border-slate-300 text-teal-600 focus:ring-teal-600"
          />
          {option.label}
          {option.hint && (
            <span className="text-slate-500">· {option.hint}</span>
          )}
        </span>
        {option.note && (
          <span className="pl-6 text-xs text-slate-500">{option.note}</span>
        )}
      </label>
    ))}
    <div className="flex justify-end gap-2">
      <Button type="button" color="white" onClick={onCancel}>
        Cancel
      </Button>
      <Button type="button" color="navy" onClick={onUpload}>
        Upload
      </Button>
    </div>
  </fieldset>
);
